import type { DetailedProposal } from '@moment/horizon-data-access/proposal'

import { OrderSide } from '@/types/enums/order'

import { allocationNetMoney } from './allocation-net-money'

export function extractOrderMarketValue(
  proposal: DetailedProposal,
  selectedAccount?: string,
  selectedSleeve?: string,
): { buyOrders: number; sellOrders: number } {
  if (!proposal || !selectedAccount) {
    return { buyOrders: 0, sellOrders: 0 }
  }

  // Get the account's allocations to filter orders
  const accountAllocations =
    proposal.accounts[selectedAccount]?.allocations || []
  const relevantAllocationIds = new Set(
    accountAllocations.map((alloc) => alloc.proposal_allocation_id),
  )

  let buyOrders = 0
  let sellOrders = 0

  if (!proposal.orders || proposal.orders.length === 0) {
    return { buyOrders: 0, sellOrders: 0 }
  }

  // Process each order
  proposal.orders.forEach((order) => {
    // Filter allocations for this account/sleeve
    const relevantAllocations = order.allocations.filter((allocation) => {
      // Check if this allocation belongs to the selected account
      if (allocation.moment_account_id !== selectedAccount) return false
      // Check if this allocation is in the account's allocation list
      if (!relevantAllocationIds.has(allocation.proposal_allocation_id))
        return false
      // If a sleeve is selected, filter by sleeve
      if (selectedSleeve && allocation.sleeve_id !== selectedSleeve)
        return false
      return true
    })

    // If there are no relevant allocations for this order, skip it
    if (relevantAllocations.length === 0) return

    // Sum up the market values for this order
    const orderMarketValue = relevantAllocations.reduce(
      (sum, allocation) => sum + allocationNetMoney(allocation),
      0,
    )

    // Add to buy or sell totals
    if (order.side === OrderSide.Buy) {
      buyOrders += orderMarketValue
    } else if (order.side === OrderSide.Sell) {
      sellOrders += orderMarketValue
    }
  })

  return { buyOrders, sellOrders }
}
